import { useEffect, useState } from 'react';

/**
 * inject the recaptcha script into the page manually
 * onError, throw an error and log it to sentry
 * onLoad, return a variable to be true
 *
 * @returns recaptchaLoaded, recaptchaError - recaptchaLoaded is true if the script is loaded, recaptchaError is an error if the script fails to load
 */
export const useRecaptchaScript = () => {
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState<Error | null>(null);

  const handleLoad = () => {
    if (window.grecaptcha) {
      setRecaptchaLoaded(true);
    } else {
      // Script loaded but grecaptcha is not available - treat as error
      const error = new Error(
        'reCAPTCHA script loaded but grecaptcha is not available',
      );
      setRecaptchaError(error);
      setRecaptchaLoaded(false);
    }
  };

  const handleError = () => {
    // If script is already loaded, silently ignore the error
    if (window.grecaptcha) {
      setRecaptchaLoaded(true);
      return;
    }

    const error = new Error('reCAPTCHA script failed to load');
    setRecaptchaError(error);
    setRecaptchaLoaded(false);
  };

  useEffect(() => {
    try {
      // Check if the script is already loaded
      if (window.grecaptcha) {
        setRecaptchaLoaded(true);
        return undefined;
      }

      // Check if the script is already being loaded
      const existingScript = document.querySelector(
        'script[src*="recaptcha/api.js"]',
      );

      if (existingScript) {
        // Script is already in DOM, wait for it to load
        existingScript.addEventListener('load', handleLoad);
        existingScript.addEventListener('error', handleError);

        return () => {
          existingScript.removeEventListener('load', handleLoad);
          existingScript.removeEventListener('error', handleError);
        };
      }

      // Create and inject the script
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;

      script.onload = () => handleLoad();
      script.onerror = () => handleError();

      try {
        document.head.appendChild(script);
      } catch {
        // If appendChild fails, set error state but don't throw
        const error = new Error(
          'Failed to append reCAPTCHA script to document',
        );
        setRecaptchaError(error);
        setRecaptchaLoaded(false);
      }

      return () => {
        // Clean up on unmount
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    } catch {
      // If script creation fails, set error state but don't throw
      const error = new Error('Failed to create reCAPTCHA script element');
      setRecaptchaError(error);
      setRecaptchaLoaded(false);
      return undefined;
    }
  }, []);

  return { recaptchaLoaded, recaptchaError };
};
